import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect, useRef, useCallback } from 'react';
import type { ActivityFeedItem, TimelineResponse } from '@ridehub/shared';
import { apiClient } from '../../lib/api-client.js';
import { useAuth } from '../auth/useAuth.js';
import { TimelineItem } from './TimelineItem.js';

type TabType = 'all' | 'following';

export function TimelineView() {
  const { dbUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);

  const fetchTimeline = useCallback(async (cursor?: string, tab: TabType = 'all') => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);

    try {
      const params = new URLSearchParams({ limit: '20' });
      if (cursor) params.set('cursor', cursor);
      if (tab === 'following') params.set('filter', 'following');

      const options = tab === 'following' ? { requireAuth: true } : undefined;
      const res = await apiClient.get<TimelineResponse>(`/timeline?${params.toString()}`, options);
      const { items: newItems, nextCursor: newCursor } = res;

      setItems((prev) => (cursor ? [...prev, ...newItems] : newItems));
      setNextCursor(newCursor);
      setHasMore(newCursor !== null);
    } catch (err) {
      console.error('[timeline] fetch error', err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // タブ切り替え時にリセットして再取得
  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    setHasMore(true);
    setInitialLoading(true);

    if (activeTab === 'following' && !dbUser) {
      setInitialLoading(false);
      return;
    }

    fetchTimeline(undefined, activeTab);
  }, [activeTab, dbUser, fetchTimeline]);

  // Intersection Observer で無限スクロール
  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && nextCursor && !isFetchingRef.current) {
          fetchTimeline(nextCursor, activeTab);
        }
      },
      { threshold: 0.1 }
    );

    const el = sentinelRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [hasMore, nextCursor, fetchTimeline, activeTab]);

  const tabs: { key: TabType; label: string }[] = [
    { key: 'all', label: '全体' },
    { key: 'following', label: 'フォロー中' },
  ];

  return (
    <div>
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 pt-3 pb-0">
          <h1 className="text-lg font-bold text-gray-900">タイムライン</h1>
        </div>
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* フォロー中タブ・未ログイン */}
      {activeTab === 'following' && !dbUser ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400 text-sm gap-2">
          <span className="text-3xl">👤</span>
          <p>ログインするとフォロー中の活動を表示できます</p>
        </div>
      ) : initialLoading ? (
        <div className="flex justify-center items-center h-40">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400 text-sm gap-2">
          <span className="text-3xl">🚲</span>
          <p>
            {activeTab === 'following'
              ? 'フォロー中のユーザーのアクティビティがありません'
              : 'まだアクティビティがありません'}
          </p>
        </div>
      ) : (
        <>
          {items.map((item) => (
            <TimelineItem key={item.id} item={item} />
          ))}

          {/* 無限スクロール番兵 */}
          <div ref={sentinelRef} className="h-4" />

          {loading && (
            <div className="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          )}

          {!hasMore && items.length > 0 && (
            <p className="text-center text-xs text-gray-400 py-4">これ以上ありません</p>
          )}
        </>
      )}
    </div>
  );
}
